import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SectionLink from "../components/landing/SectionLink";
import ZylenHeader from "../components/landing/ZylenHeader";
import ZylenFooter from "../components/landing/ZylenFooter";
import "../components/landing/ZylenLanding.css";

export default function ZylenLanding() {
  const [voiceFilter, setVoiceFilter] = useState("all");
  const carouselRef = useRef(null);
  const handleMainClick = useCallback((event) => {
    const filter = event.target.closest("[data-f]");
    if (filter) {
      const value = filter.dataset.f;
      setVoiceFilter(value);
      carouselRef.current?.scrollTo({ left: 0, behavior: "smooth" });
    }
    const arrow = event.target.closest("#v-prev, #v-next");
    if (arrow) {
      const track = document.getElementById("track");
      const card = track?.querySelector(".quote:not([hidden])");
      const amount = (card?.getBoundingClientRect().width || 362) + 18;
      track?.scrollBy({
        left: arrow.id === "v-prev" ? -amount : amount,
        behavior: "smooth",
      });
    }
  }, []);
  return (
    <div className="zylen-v3" data-voice-filter={voiceFilter}>
      <ZylenHeader />
      <main id="top" onClick={handleMainClick}>
        {/* HERO */}
        <section className="hero">
          <div className="wrap">
            <div className="hero-copy">
              <span className="eyebrow">a home for filmmakers</span>
              <h1>
                find work,
                <br />
                hire creatives,
                <br />
                <span>and get paid safely.</span>
              </h1>
              <p className="lead">
                businesses and individuals post jobs, filmmakers apply, and the
                whole job runs in the app, from the brief to the payment.
              </p>
              <div className="stores">
                <a
                  className="badge"
                  href="https://apps.apple.com/us/app/zylen/id6751733170"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <img
                    src={require("../assets/landingpage/images/logos/app-store-badge.png")}
                    alt="Download on the App Store"
                  />
                </a>
                <a
                  className="badge"
                  href="https://play.google.com/store/apps/details?id=com.zylenapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <img
                    src={require("../assets/landingpage/images/logos/google-play-badge.png")}
                    alt="Get it on Google Play"
                  />
                </a>
              </div>
              <div className="soon">now on the App Store and Google Play</div>
            </div>
            <div className="hero-art" aria-hidden="true">
              <div className="phone p2">
                <img
                  src={require("../assets/landingpage/images/app-screens/app-6-see-who-applies.jpg")}
                  alt=""
                />
              </div>
              <div className="phone p1">
                <img
                  src={require("../assets/landingpage/images/app-screens/app-1-find-film-work.jpg")}
                  alt=""
                />
              </div>
            </div>
          </div>
        </section>
        {/* WHICH SIDE */}
        <section className="split" aria-label="which side are you on">
          <div className="wrap">
            <SectionLink className="sp" to="for-filmmakers">
              <span className="sp-tag">for filmmakers</span>
              <b>I'm looking for work</b>
              <span className="sp-sub">
                paid jobs with the budget and dates shown before you apply
              </span>
              <span className="sp-go">
                see the job board{" "}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8h10M9 4l4 4-4 4"></path>
                </svg>
              </span>
            </SectionLink>
            <SectionLink className="sp hire" to="for-hiring">
              <span className="sp-tag">for hiring</span>
              <b>I want to hire someone</b>
              <span className="sp-sub">
                post a job for free, then watch their actual work before you
                decide
              </span>
              <span className="sp-go">
                see how hiring works{" "}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8h10M9 4l4 4-4 4"></path>
                </svg>
              </span>
            </SectionLink>
          </div>
        </section>
        <section className="facts" aria-label="zylen in numbers">
          <div className="wrap">
            <ul>
              <li>
                <b>600+</b>
                <span>filmmakers in the zylen community</span>
              </li>
              <li>
                <b>80 to 100</b>
                <span>filmmakers at every monthly event</span>
              </li>
              <li>
                <b>monthly</b>
                <span>in-person events since March 2026</span>
              </li>
            </ul>
          </div>
        </section>
        {/* JOB BOARD */}
        <section className="sec board" id="job-board">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">the job board</span>
              <h2 className="h2">
                one board.
                <br />
                both sides of the shoot.
              </h2>
              <p className="lead">
                whether you need a crew for a shoot, a video made for your
                business, or you're looking for your next project, zylen
                connects the two.
              </p>
            </div>
            <div className="board-grid">
              <article className="lane" id="for-filmmakers">
                <div>
                  <span className="tag">for filmmakers</span>
                  <h3>find your next project.</h3>
                  <ul>
                    <li>
                      <span>
                        <b>jobs matched to what you do,</b> from editors to
                        directors
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>budget and dates up front,</b> before you apply
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>apply in about a minute</b> with your showreel
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>get paid in the app,</b> every job tracked
                      </span>
                    </li>
                  </ul>
                  <SectionLink className="go" to="get-the-app">
                    find film work{" "}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 8h10M9 4l4 4-4 4"></path>
                    </svg>
                  </SectionLink>
                </div>
                <div className="peek">
                  <img
                    src={require("../assets/landingpage/images/app-screens/app-2-see-the-whole-brief.jpg")}
                    alt="a job brief on zylen, with budget, work days and deadline"
                  />
                </div>
              </article>
              <article className="lane hire" id="for-hiring">
                <div>
                  <span className="tag">for hiring</span>
                  <h3>hire the right people.</h3>
                  <ul>
                    <li>
                      <span>
                        <b>post a job,</b> a crew call or a whole video
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>see who applies,</b> then shortlist and message
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>check their work,</b> projects and real reviews
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>pay in the app,</b> one payment, no invoices to chase
                      </span>
                    </li>
                  </ul>
                  <SectionLink className="go" to="get-the-app">
                    post a job{" "}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 8h10M9 4l4 4-4 4"></path>
                    </svg>
                  </SectionLink>
                </div>
                <div className="peek">
                  <img
                    src={require("../assets/landingpage/images/app-screens/app-5-post-a-job.jpg")}
                    alt="the hiring home screen on zylen, with live job posts and new applicants"
                  />
                </div>
              </article>
            </div>
          </div>
        </section>
        {/* FEATURES */}
        <section className="sec features on-paint" id="features">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">features</span>
              <h2 className="h2">on the app.</h2>
            </div>
            <div className="feat-row">
              <div className="row-label">for filmmakers</div>
              <div className="tiles">
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-1-find-film-work.jpg")}
                      alt="find work screen"
                    />
                  </div>
                  <div>
                    <h3>find film work</h3>
                    <p>jobs matched to what you do</p>
                  </div>
                </article>
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-2-see-the-whole-brief.jpg")}
                      alt="job brief screen"
                    />
                  </div>
                  <div>
                    <h3>see the whole brief</h3>
                    <p>budget and dates up front</p>
                  </div>
                </article>
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-3-show-your-work.jpg")}
                      alt="a filmmaker's profile as a hirer sees it on Discover"
                    />
                  </div>
                  <div>
                    <h3>show your work</h3>
                    <p>showreel, projects, reviews</p>
                  </div>
                </article>
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-4-get-paid.jpg")}
                      alt="earnings screen"
                    />
                  </div>
                  <div>
                    <h3>get paid</h3>
                    <p>every job tracked in one place</p>
                  </div>
                </article>
              </div>
            </div>
            <div className="feat-row hire">
              <div className="row-label">for hiring</div>
              <div className="tiles">
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-5-post-a-job.jpg")}
                      alt="post a job screen"
                    />
                  </div>
                  <div>
                    <h3>post a job</h3>
                    <p>live in a few minutes</p>
                  </div>
                </article>
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-6-see-who-applies.jpg")}
                      alt="applicants screen"
                    />
                  </div>
                  <div>
                    <h3>see who applies</h3>
                    <p>shortlist, message, hire</p>
                  </div>
                </article>
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-7-check-their-work.jpg")}
                      alt="projects and reviews screen"
                    />
                  </div>
                  <div>
                    <h3>check their work</h3>
                    <p>projects and real reviews</p>
                  </div>
                </article>
                <article className="tile">
                  <div className="shot">
                    <img
                      src={require("../assets/landingpage/images/app-screens/app-8-pay-safely.jpg")}
                      alt="secure and hire screen"
                    />
                  </div>
                  <div>
                    <h3>pay in the app</h3>
                    <p>one payment, no invoices to chase</p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>
        {/* CTA 1 */}
        <section
          className="sec band"
          style={{ paddingBlock: "clamp(56px,7vw,90px)" }}
        >
          <div className="wrap">
            <div className="cta-light">
              <div className="txt">
                <h2 className="three">
                  <span>find work.</span>
                  <span>hire creatives.</span>
                  <span>start your zylen journey today!</span>
                </h2>
                <div className="stores">
                  <a
                    className="badge"
                    href="https://apps.apple.com/us/app/zylen/id6751733170"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                  >
                    <img
                      src={require("../assets/landingpage/images/logos/app-store-badge.png")}
                      alt="Download on the App Store"
                    />
                  </a>
                  <a
                    className="badge"
                    href="https://play.google.com/store/apps/details?id=com.zylenapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it on Google Play"
                  >
                    <img
                      src={require("../assets/landingpage/images/logos/google-play-badge.png")}
                      alt="Get it on Google Play"
                    />
                  </a>
                </div>
                <div className="soon">now on the App Store and Google Play</div>
              </div>
              <div className="paint">
                <img
                  src={require("../assets/landingpage/images/logos/zylen-app-icon.png")}
                  alt="the zylen app icon"
                />
              </div>
            </div>
          </div>
        </section>
        {/* VOICES */}
        <section className="sec voices on-paint" id="voices">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">voices</span>
              <h2 className="h2">in their own words.</h2>
              <p className="lead">
                hirers and creatives on working through zylen.
              </p>
            </div>
            <div className="v-bar">
              <div className="filters" role="group" aria-label="filter voices">
                <button
                  type="button"
                  id="f-all"
                  data-f="all"
                  aria-pressed={voiceFilter === "all"}
                >
                  everyone
                </button>
                <button
                  type="button"
                  id="f-h"
                  data-f="h"
                  aria-pressed={voiceFilter === "h"}
                >
                  hirers
                </button>
                <button
                  type="button"
                  id="f-c"
                  data-f="c"
                  aria-pressed={voiceFilter === "c"}
                >
                  creatives
                </button>
              </div>
              <div className="arrows">
                <button type="button" id="v-prev" aria-label="previous">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M13 8H3M7 4L3 8l4 4"></path>
                  </svg>
                </button>
                <button type="button" id="v-next" aria-label="next">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 8h10M9 4l4 4-4 4"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="track" id="track" ref={carouselRef}>
              <figure className="quote h" data-k="h">
                <div className="mark">“</div>
                <p>
                  I came to zylen needing to fill three video editing roles at
                  once, something I'd normally set aside weeks for.{" "}
                  <b>I had someone hired in 48 hours.</b> The standard of
                  applicants received from this group is excellent, which isn't
                  something I can say about any other hiring route I've tried.
                  If you're looking to hire the best creative talent in South
                  Africa, this is the place to be.
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-quinton.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Quinton Weber</div>
                    <div className="ds">founder, Brand Vista</div>
                    <span className="side">hirer</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote" data-k="c">
                <div className="mark">“</div>
                <p>
                  Securing work through zylen was incredibly smooth and
                  straightforward. Filled out my info on the job posting and a
                  short motivation, got a call from the employer the next week
                  and <b>landed work I would have never come across</b> in my
                  own capacity. So huge shoutout to this platform for making
                  these connections possible!
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-mayur.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Mayur Vallabhjee</div>
                    <div className="ds">video editor and filmmaker</div>
                    <span className="side">creative</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote h" data-k="h">
                <div className="mark">“</div>
                <p>
                  zylen was incredibly helpful when I needed to find a video
                  editor at very short notice for the 48H Film Project. Joshua
                  was highly efficient, responsive, and made the process easy by
                  sending through portfolios from different suitable candidates.{" "}
                  <b>I was able to find the right editor quickly</b> and with
                  minimal hassle. Really impressed with how effective the
                  platform is and would definitely recommend zylen!
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-gizela.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Gizela du Plessis</div>
                    <div className="ds">creative, 48H Film Project</div>
                    <span className="side">hirer</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote" data-k="c">
                <div className="mark">“</div>
                <p>
                  I found zylen super helpful for landing video editing work.
                  The application process is straightforward and easy to
                  navigate, which I really appreciate. What impressed me most
                  was <b>how quickly things moved.</b> Working with the platform
                  and the clients I've connected with through it has been a
                  pleasure. If you're a video editor looking for reliable
                  freelance opportunities, I'd definitely recommend giving zylen
                  a shot
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-joel.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Joel Kempthorne</div>
                    <div className="ds">video editor and filmmaker</div>
                    <span className="side">creative</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote h" data-k="h">
                <div className="mark">“</div>
                <p>
                  zylen is an incredible project and a fantastic service! For
                  someone that has no touch when it comes to capturing moments
                  on camera, I felt at ease being able to get in touch and be
                  provided help to <b>capture an incredibly special moment.</b>{" "}
                  I look forward to using the platform in the future, knowing
                  that the process is smooth and easy!
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-sean.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Sean van Oudtshoorn</div>
                    <div className="ds">
                      hired a photographer for his proposal
                    </div>
                    <span className="side">hirer</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote" data-k="c">
                <div className="mark">“</div>
                <p>
                  Being a freelancer is extremely rewarding but can have massive
                  fluctuations. Being switched on all the time is tiring.
                  Networking is a skill on its own. That's why I am super
                  grateful for zylen, <b>bringing the client to us.</b> Grateful
                  how easy the process is and having the choice to choose which
                  jobs to apply for, and clients we really align with. Super
                  excited to see where zylen goes and helping freelancers all
                  over!
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-alistair.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Alistair Yates</div>
                    <div className="ds">photographer</div>
                    <span className="side">creative</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote" data-k="c">
                <div className="mark">“</div>
                <p>
                  zylen connected me with a team competing in a 48-hour film
                  competition who needed an editor. Communication was quick and
                  clear throughout, and Joshua stayed on top of everything from
                  start to finish. <b>The whole process was smooth,</b> and it
                  turned out to be a cool experience.
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-chenge.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Chenge Chitima</div>
                    <div className="ds">video editor and filmmaker</div>
                    <span className="side">creative</span>
                  </div>
                </footer>
              </figure>
              <figure className="quote" data-k="c">
                <div className="mark">“</div>
                <p>
                  I have never met so many like minded people in the creative
                  space than I did at one zylen event. I managed to land{" "}
                  <b>my first job as a solo creator</b> through the extremely
                  active zylen community, where jobs are posted regularly.
                  Amazing team, amazing people.
                </p>
                <footer>
                  <img
                    src={require("../assets/landingpage/images/photos/portrait-edwards.jpg")}
                    alt=""
                  />
                  <div>
                    <div className="nm">Joshua Edwards</div>
                    <div className="ds">filmmaker</div>
                    <span className="side">creative</span>
                  </div>
                </footer>
              </figure>
            </div>
          </div>
        </section>
        {/* EVENTS */}
        <section className="sec events" id="events">
          <div className="wrap">
            <div className="sec-head ev-head">
              <div style={{ display: "grid", gap: "16px" }}>
                <span className="eyebrow">in person</span>
                <h2 className="h2">monthly events.</h2>
              </div>
              <div className="ev-cta">
                <p className="lead">
                  80 to 100 filmmakers in one room, every month in Cape Town.
                </p>
              </div>
            </div>
            <div className="mosaic">
              <figure className="m1">
                <img
                  src={require("../assets/landingpage/images/photos/event-crowd-colour.jpg")}
                  alt="a full room of filmmakers at a zylen monthly event"
                />
              </figure>
              <figure className="m2">
                <img
                  src={require("../assets/landingpage/images/photos/event-group-gallery.jpg")}
                  alt="a group photo under festoon lights"
                />
              </figure>
              <figure className="m3">
                <img
                  src={require("../assets/landingpage/images/photos/event-group-wide.jpg")}
                  alt="everyone together at a zylen event"
                />
              </figure>
              <figure className="m4">
                <img
                  src={require("../assets/landingpage/images/photos/event-mingling.jpg")}
                  alt="filmmakers meeting each other at a zylen event"
                />
              </figure>
            </div>
            <div className="ev-more">
              <Link className="btn solid ev-big" to="/events">
                join the next event{" "}
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8h10M9 4l4 4-4 4"></path>
                </svg>
              </Link>
            </div>
          </div>
        </section>
        {/* START TODAY */}
        <section className="keep" id="start-today">
          <div className="wrap">
            <div className="keep-card">
              <div>
                <span className="eyebrow">ready when you are</span>
                <h2 style={{ marginTop: "14px" }}>
                  get your first job, or hire someone today.
                </h2>
                <p>
                  download zylen, set up your profile in a few minutes, and you
                  are on the board with the rest of the community.
                </p>
              </div>
              <div>
                <div className="stores">
                  <a
                    className="badge"
                    href="https://apps.apple.com/us/app/zylen/id6751733170"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                  >
                    <img
                      src={require("../assets/landingpage/images/logos/app-store-badge.png")}
                      alt="Download on the App Store"
                    />
                  </a>
                  <a
                    className="badge"
                    href="https://play.google.com/store/apps/details?id=com.zylenapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it on Google Play"
                  >
                    <img
                      src={require("../assets/landingpage/images/logos/google-play-badge.png")}
                      alt="Get it on Google Play"
                    />
                  </a>
                </div>
                <p className="fine">
                  free to join. free to apply. free to post a job.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* TEAM */}
        <section className="sec team on-paint" id="team">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">the team</span>
              <h2 className="h2">the people behind zylen.</h2>
              <p className="lead" style={{ maxWidth: "none" }}>
                zylen is run by filmmakers who use it themselves.
              </p>
            </div>
            <div className="team-shot">
              <img
                className="couple"
                src={require("../assets/landingpage/images/photos/team-couple.jpg")}
                alt="Joshua Farrer and Daniah de Villiers"
                width="2400"
                height="1350"
              />
            </div>
            <div className="people">
              <article className="person">
                <img
                  className="portrait"
                  src={require("../assets/landingpage/images/photos/portrait-joshua-team.jpg")}
                  alt="Joshua Farrer"
                  width="1200"
                  height="1500"
                />
                <div className="txt">
                  <h3>Joshua Farrer</h3>
                  <div className="role">founder and CEO</div>
                  <div className="socials">
                    <a
                      href="https://www.instagram.com/joshua_farrer/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Joshua on Instagram"
                      title="Instagram"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="5"></rect>
                        <circle cx="12" cy="12" r="4"></circle>
                        <circle
                          cx="17.5"
                          cy="6.5"
                          r="1"
                          fill="currentColor"
                          stroke="none"
                        ></circle>
                      </svg>
                    </a>
                    <a
                      href="https://www.linkedin.com/in/joshua-farrer-4342b9375"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Joshua on LinkedIn"
                      title="LinkedIn"
                    >
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.75h4v11H3v-11zm6.5 0h3.83v1.5h.05c.53-1 1.84-2.06 3.79-2.06 4.05 0 4.8 2.67 4.8 6.13v5.43h-4v-4.82c0-1.15-.02-2.63-1.6-2.63-1.6 0-1.85 1.25-1.85 2.55v4.9h-4v-11z"></path>
                      </svg>
                    </a>
                  </div>
                  <p className="bio">
                    Joshua Farrer is a 24-year-old filmmaker from South Africa,
                    a visionary and an entrepreneur with a zeal for watching his
                    ideas come alive. He made his first video at the age of 8
                    and has been filming ever since, building friendships with
                    filmmakers and creators around the world.
                  </p>
                  <p className="bio">
                    Joshua has a great love for people and sparks new
                    connections wherever he goes. Through his videos he sets out
                    to tell stories that have a lasting impact. Beyond the lens,
                    he is the founder and CEO of zylen, a home for filmmakers
                    built by a filmmaker, where his creative and entrepreneurial
                    sides meet in helping others bring their own creative dreams
                    to life.
                  </p>
                </div>
              </article>
              <article className="person">
                <img
                  className="portrait"
                  src={require("../assets/landingpage/images/photos/portrait-daniah-team.jpg")}
                  alt="Daniah de Villiers"
                  width="1200"
                  height="1500"
                />
                <div className="txt">
                  <h3>Daniah de Villiers</h3>
                  <div className="role">co-founder</div>
                  <div className="socials">
                    <a
                      href="https://www.instagram.com/daniahdevilliers_official/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Daniah on Instagram"
                      title="Instagram"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="5"></rect>
                        <circle cx="12" cy="12" r="4"></circle>
                        <circle
                          cx="17.5"
                          cy="6.5"
                          r="1"
                          fill="currentColor"
                          stroke="none"
                        ></circle>
                      </svg>
                    </a>
                    <a
                      href="https://www.linkedin.com/in/daniah-de-villiers-992305117/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Daniah on LinkedIn"
                      title="LinkedIn"
                    >
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.75h4v11H3v-11zm6.5 0h3.83v1.5h.05c.53-1 1.84-2.06 3.79-2.06 4.05 0 4.8 2.67 4.8 6.13v5.43h-4v-4.82c0-1.15-.02-2.63-1.6-2.63-1.6 0-1.85 1.25-1.85 2.55v4.9h-4v-11z"></path>
                      </svg>
                    </a>
                  </div>
                  <p className="bio">
                    Daniah de Villiers is a South African actress, singer and
                    creative who has been working professionally in the
                    entertainment industry since the age of 12. She gained
                    international recognition as the lead in{" "}
                    <em>Mia and the White Lion</em>, a role that saw her spend
                    three years filming alongside a real lion.
                  </p>
                  <p className="bio">
                    Since then, Daniah has built an impressive portfolio of both
                    South African and international film and television work,
                    including <em>Redeeming Love</em>,{" "}
                    <em>The Dating Game Killer</em>, <em>Desert Rose</em>,{" "}
                    <em>Binnelanders</em> and <em>Die Kantoor</em>. With a
                    passion for storytelling and creativity, she continues to
                    explore opportunities across the entertainment and creative
                    industries.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
        {/* CONTACT */}
        <section className="sec contact" id="contact">
          <div className="wrap">
            <div className="contact-card">
              <div>
                <span className="eyebrow">get in touch</span>
                <h2 style={{ marginTop: "14px" }}>let's connect!</h2>
                <p>
                  whether it's feedback, collaboration, or support, we're here
                  to help.
                </p>
              </div>
              <div className="contact-actions">
                <a className="btn solid" href="mailto:contact@zylen.app">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  >
                    <rect x="2.5" y="4.5" width="15" height="11" rx="2"></rect>
                    <path d="M3 5.5l7 5.5 7-5.5"></path>
                  </svg>
                  contact@zylen.app
                </a>

                <a
                  className="btn ghost"
                  href="https://www.youtube.com/@zylen_app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M23 7.2a3 3 0 0 0-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12a31 31 0 0 0 .5 4.8 3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-4.8 31 31 0 0 0-.5-4.8zM9.7 15V9l5.8 3-5.8 3z" />
                  </svg>
                  youtube
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* CTA 2 */}
        <section className="cta" id="get-the-app">
          <div className="wrap">
            <span className="markbig">
              <svg
                viewBox="231.93 258.34 101.15 78.60"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M315.96,274.4c1.76,17.07-11.53,17.7-23.2,21.79-6.33,2.22-22.77,10.76-24.67,17.37-.87,3.01-.24,6.76,2.73,8.48,7.44,4.32,48.83-21.86,58.38-10.54,5.07,6.02,6.26,21.4-3.17,23-10.3,1.75-45.3-17.45-57.97-5.77-4.02,3.71-7.61,7.26-13.64,8.06-14.67,1.94-21.04-15.97-10.35-25.13,3.93-3.37,7.61-3.63,12-5.54,10.09-4.39,17.38-10.45,21.64-20.83,3.33-8.1,2.34-16.58,9.54-22.77,10.83-9.31,27.28-2.02,28.71,11.89Z"></path>
                <path d="M270.92,272.93c0,6.86-16.34,14.59-24.4,14.59s-14.59-6.53-14.59-14.59,6.53-14.59,14.59-14.59,24.4,7.81,24.4,14.59Z"></path>
              </svg>
            </span>
            <h2 className="three">
              <span>find work.</span>
              <span>hire creatives.</span>
              <span>start your zylen journey today!</span>
            </h2>
            <div className="stores">
              <a
                className="badge"
                href="https://apps.apple.com/us/app/zylen/id6751733170"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
              >
                <img
                  src={require("../assets/landingpage/images/logos/app-store-badge.png")}
                  alt="Download on the App Store"
                />
              </a>
              <a
                className="badge"
                href="https://play.google.com/store/apps/details?id=com.zylenapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
              >
                <img
                  src={require("../assets/landingpage/images/logos/google-play-badge.png")}
                  alt="Get it on Google Play"
                />
              </a>
            </div>
            <div className="soon">now on the App Store and Google Play</div>
          </div>
        </section>
      </main>
      <ZylenFooter />
    </div>
  );
}
